// Shape guards for API responses.
// These do NOT block rendering — they log warnings when the server payload
// doesn't match the shape the frontend depends on. The application continues
// regardless. The goal is early detection, not enforcement.

type ShapeWarning = { field: string; expected: string; got: string };

function describe(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (Array.isArray(value)) return 'array';
  return typeof value;
}

function warn(source: string, warnings: ShapeWarning[]): void {
  for (const w of warnings) {
    console.warn(
      `[shape-guard] ${source}: field "${w.field}" — expected ${w.expected}, got ${w.got}`
    );
  }
}

// --- Catalog (GET /:apikey/plans) ---

export function guardCatalogResponse(data: unknown): void {
  if (!data || typeof data !== 'object') {
    console.warn('[shape-guard] catalog: response is not an object');
    return;
  }

  const warnings: ShapeWarning[] = [];
  const d = data as Record<string, unknown>;

  // plans must be an array
  if ('plans' in d && !Array.isArray(d.plans)) {
    warnings.push({ field: 'plans', expected: 'array', got: describe(d.plans) });
  }

  // Each plan.features must be an object (not an array)
  if (Array.isArray(d.plans)) {
    for (let i = 0; i < d.plans.length; i++) {
      const plan = d.plans[i] as Record<string, unknown>;
      if (!plan || typeof plan !== 'object') continue;

      if ('features' in plan && plan.features !== null && plan.features !== undefined) {
        if (Array.isArray(plan.features) || typeof plan.features !== 'object') {
          warnings.push({
            field: `plans[${i}].features`,
            expected: 'object { key: boolean }',
            got: describe(plan.features),
          });
        }
      }

      // pricing[].currency must be an object with .code
      if (Array.isArray(plan.pricing)) {
        for (let j = 0; j < plan.pricing.length; j++) {
          const pr = plan.pricing[j] as Record<string, unknown>;
          if (pr && 'currency' in pr && pr.currency !== null && pr.currency !== undefined) {
            if (typeof pr.currency !== 'object' || !('code' in (pr.currency as any))) {
              warnings.push({
                field: `plans[${i}].pricing[${j}].currency`,
                expected: 'object { code, name?, symbol? }',
                got: describe(pr.currency),
              });
            }
          }
        }
      }
    }
  }

  // vocabulary.actions must carry name and letter
  if (d.vocabulary && typeof d.vocabulary === 'object') {
    const vocab = d.vocabulary as Record<string, unknown>;
    if (Array.isArray(vocab.actions)) {
      for (let i = 0; i < vocab.actions.length; i++) {
        const a = vocab.actions[i] as Record<string, unknown>;
        if (!a || typeof a !== 'object') continue;
        if (!('name' in a)) {
          warnings.push({
            field: `vocabulary.actions[${i}]`,
            expected: '{ name, letter }',
            got: `missing "name" (keys: ${Object.keys(a).join(', ')})`,
          });
        }
        if (!('letter' in a)) {
          warnings.push({
            field: `vocabulary.actions[${i}]`,
            expected: '{ name, letter }',
            got: `missing "letter" (keys: ${Object.keys(a).join(', ')})`,
          });
        }
      }
    }
  }

  if (warnings.length > 0) {
    warn('catalog', warnings);
  }
}

// --- Snapshot (GET /:apikey/subscriptions/:id/snapshot) ---

export function guardSnapshotResponse(data: unknown): void {
  if (!data || typeof data !== 'object') {
    console.warn('[shape-guard] snapshot: response is not an object');
    return;
  }

  const warnings: ShapeWarning[] = [];
  const d = data as Record<string, unknown>;

  // subscription must exist and be an object
  if ('subscription' in d) {
    if (d.subscription === null || d.subscription === undefined) {
      // acceptable — no subscription yet
    } else if (typeof d.subscription !== 'object') {
      warnings.push({ field: 'subscription', expected: 'object', got: describe(d.subscription) });
    } else {
      const sub = d.subscription as Record<string, unknown>;

      // boundary must be inside subscription
      if ('boundary' in sub && sub.boundary !== null && sub.boundary !== undefined) {
        if (typeof sub.boundary !== 'object') {
          warnings.push({ field: 'subscription.boundary', expected: 'object', got: describe(sub.boundary) });
        }
      }

      // id must be present for route calls
      if (!('id' in sub) && !('_id' in sub)) {
        warnings.push({ field: 'subscription.id', expected: 'string', got: 'missing' });
      }
    }
  }

  // boundary at top level is the old wrong shape
  if ('boundary' in d && d.boundary !== undefined) {
    warnings.push({
      field: 'boundary (top level)',
      expected: 'absent (should be inside subscription)',
      got: describe(d.boundary),
    });
  }

  if (warnings.length > 0) {
    warn('snapshot', warnings);
  }
}
